// Globals
import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { updateAdminFilters, getAdminsList, selectAdminFilters } from '../../feature';

// Components
import { StyledFilterRow, StyledSelect, StyledInput } from './AdminFiltersContainerStyles';

// Helpers
import { GetAdminsListDTO } from '../../dtos';
import { AdminFiltersFields } from '../../helpers/types';
import { getRoleOptions, getStatusOptions } from '../../helpers/getOptions';

type IAdminFiltersContainerProps = {
  children?: ReactNode;
  className?: string;
};

const AdminFiltersContainer = ({ children, className }: IAdminFiltersContainerProps) => {
  const { t } = useTranslation(['admins']);
  const dispatch = useAppDispatch();
  const adminFilters = useAppState(selectAdminFilters);

  const handleDebounceSearch = useCallback(
    debounce(async (searchParams) => {
      const dto = new GetAdminsListDTO(searchParams);
      dispatch(getAdminsList(dto));
    }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...adminFilters, [name]: value || undefined };

    dispatch(updateAdminFilters(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleSelect = (name: string) => (value: unknown) => {
    const newQuery = {
      ...adminFilters,
      [name]: value,
    };
    dispatch(updateAdminFilters(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th></th>
        <th></th>
        <th>
          <StyledSelect
            options={getRoleOptions(t)}
            placeholder={t('admins_list_role')}
            onChange={handleSelect(AdminFiltersFields.ROLE)}
            allowClear
          />
        </th>
        <th>
          <StyledInput
            placeholder={t('admins_list_email')}
            name={AdminFiltersFields.EMAIL}
            onChange={handleSearch}
            value={adminFilters?.[AdminFiltersFields.EMAIL] ?? ''}
            allowClear
          />
        </th>
        <th></th>
        <th>
          <StyledSelect
            options={getStatusOptions(t)}
            placeholder={t('admins_list_status')}
            onChange={handleSelect(AdminFiltersFields.STATUS)}
            allowClear
          />
        </th>
        <th />
      </StyledFilterRow>
    </thead>
  );
};

export default memo(AdminFiltersContainer);
